import { Link } from "react-router-dom";
import { FaCode, FaRocket, FaTrophy, FaArrowRight, FaPlay } from "react-icons/fa";
import { useTheme } from "../Theme/ThemeContext";

export default function CoursePromoAd({ variant = "default" }) {
  const { currentTheme } = useTheme();
  const isCompact = variant === "sidebar" || variant === "inline" || variant === "compact";

  // Compact / sidebar version
  if (isCompact) {
    return (
      <Link
        to="/courses"
        className={`group block relative overflow-hidden rounded-2xl bg-gradient-to-br ${currentTheme.colors.gradient} p-5 shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] mb-4`}
      >
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 -left-4 w-24 h-24 bg-white rounded-full mix-blend-overlay filter blur-xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-32 h-32 bg-yellow-300 rounded-full mix-blend-overlay filter blur-xl animate-pulse delay-700" />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/20 text-white text-xs font-semibold backdrop-blur-sm">
              <FaRocket className="w-3 h-3" /> NEW
            </span>
          </div>

          <h3 className="text-lg font-bold text-white mb-2 group-hover:translate-x-1 transition-transform">
            Master Golang 🚀
          </h3>
          <p className="text-sm text-white/80 mb-4">
            From zero to hero with hands-on coding challenges
          </p>

          <div className="inline-flex items-center gap-2 text-white font-semibold text-sm group-hover:gap-3 transition-all">
            Start Learning <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </Link>
    );
  }

  // Full / banner version
  return (
    <div className={`relative overflow-hidden rounded-3xl bg-gradient-to-r ${currentTheme.colors.gradient} p-1`}>
      {/* Animated border glow */}
      <div className={`absolute inset-0 bg-gradient-to-r ${currentTheme.colors.gradient} opacity-50 blur-xl animate-pulse`} />

      <div className="relative rounded-[22px] bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6 sm:p-8 overflow-hidden">
        {/* Floating animated shapes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-20 h-20 bg-accent-500/20 rounded-full blur-2xl animate-float" />
          <div className="absolute bottom-10 right-20 w-32 h-32 bg-royal-500/20 rounded-full blur-2xl animate-float-delayed" />
          <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-emerald-500/20 rounded-full blur-2xl animate-float" />

          {/* Code symbols floating */}
          <div className="absolute top-6 right-10 text-4xl opacity-10 animate-bounce-slow">{"{ }"}</div>
          <div className="absolute bottom-8 left-16 text-3xl opacity-10 animate-bounce-slow delay-500">{"</>"}</div>
          <div className="absolute top-1/3 right-1/4 text-2xl opacity-10 animate-bounce-slow delay-1000">{"()"}</div>
        </div>

        <div className="relative z-10 flex flex-col lg:flex-row items-center gap-8">
          {/* Left: Content */}
          <div className="flex-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-yellow-400/20 to-orange-400/20 border border-yellow-400/30 text-yellow-300 text-sm font-semibold mb-4 animate-pulse">
              <FaRocket className="w-4 h-4" />
              <span>Limited Time: Free Access to First 5 Chapters!</span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4 leading-tight">
              Master{" "}
              <span className="bg-gradient-to-r from-accent-400 via-emerald-400 to-royal-400 bg-clip-text text-transparent animate-gradient">
                Golang
              </span>{" "}
              <br className="hidden sm:block" />
              From Zero to Hero
            </h2>

            <p className="text-lg text-gray-300 mb-6 max-w-xl">
              Interactive coding challenges, real-time execution, hidden test cases, and a structured path to land your dream job.
            </p>

            {/* Features */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-3 mb-8">
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 text-white text-sm">
                <FaCode className="text-accent-400" />
                <span>Live Coding</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 text-white text-sm">
                <FaTrophy className="text-amber-400" />
                <span>Hidden Tests</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 text-white text-sm">
                <FaRocket className="text-emerald-400" />
                <span>Get Hired</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-4">
              <Link
                to="/courses"
                className={`group inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r ${currentTheme.colors.gradient} text-white font-semibold shadow-lg ${currentTheme.colors.glow} hover:shadow-xl hover:scale-105 transition-all duration-300`}
              >
                <FaPlay className="w-4 h-4 group-hover:scale-110 transition-transform" />
                Start Learning Free
              </Link>
              <Link
                to="/courses"
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold hover:bg-white/20 hover:scale-105 transition-all duration-300"
              >
                View Curriculum
                <FaArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>

          {/* Right: Visual element */}
          <div className="hidden lg:flex flex-col items-center justify-center relative">
            <div className="relative w-64 h-64">
              {/* Glowing ring */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 animate-spin-slow opacity-20 blur-md" />
              
              {/* Central icon */}
              <div className="absolute inset-4 rounded-full bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center shadow-2xl border border-white/10">
                <div className="text-center">
                  <div className="text-6xl mb-2">🐹</div>
                  <div className="text-white font-bold text-xl">Golang</div>
                  <div className="text-gray-400 text-sm">Bootcamp</div>
                </div>
              </div>

              {/* Orbiting elements */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 w-12 h-12 rounded-full bg-gradient-to-br from-accent-400 to-accent-600 flex items-center justify-center shadow-lg animate-bounce-slow">
                <FaCode className="text-white text-lg" />
              </div>
              <div className="absolute bottom-4 left-0 w-10 h-10 rounded-full bg-gradient-to-br from-royal-400 to-royal-600 flex items-center justify-center shadow-lg animate-bounce-slow delay-300">
                <FaTrophy className="text-white text-sm" />
              </div>
              <div className="absolute bottom-4 right-0 w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg animate-bounce-slow delay-700">
                <FaRocket className="text-white text-sm" />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom stats bar */}
        <div className="relative z-10 mt-8 pt-6 border-t border-white/10">
          <div className="flex flex-wrap justify-center lg:justify-start gap-8 text-center lg:text-left">
            <div>
              <div className="text-2xl font-bold text-white">50+</div>
              <div className="text-sm text-gray-400">Coding Challenges</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-white">10+</div>
              <div className="text-sm text-gray-400">Chapters</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-white">100%</div>
              <div className="text-sm text-gray-400">Hands-on</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-accent-400">FREE</div>
              <div className="text-sm text-gray-400">First 5 Chapters</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
